import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type {
  BrowserExportKind,
  ExportArtifactOwner,
} from "@constructionautomation/backend/convex/lib/exportArtifactNames";
import type { FunctionArgs } from "convex/server";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { useCallback } from "react";

import { useCanEdit } from "@/lib/capabilities/use-can-edit";
import { notifyError } from "@/lib/notify-error";

export type { ExportArtifactOwner };

type BrowserExportOptions = FunctionArgs<typeof api.exportArtifacts.recordBrowserExport>["options"];

/**
 * Saved export file names for one job, read from its export artifacts. Saving is best effort:
 * a failure is reported but never blocks the download, and reviewers simply don't save.
 */
export function useExportFilenames(owner: ExportArtifactOwner | null) {
  const canSave = useCanEdit() && owner !== null;
  const { data: savedNames } = useQuery(
    convexQuery(api.exportArtifacts.listSavedFilenames, owner ? { owner } : "skip")
  );
  const setFilename = useMutation(api.exportArtifacts.setFilename);
  const recordBrowserExport = useMutation(api.exportArtifacts.recordBrowserExport);

  /** The saved name for a deliverable (see `exportNameTargetKey`), or its default. */
  const resolve = useCallback(
    (targetKey: string, defaultName: string) => savedNames?.[targetKey] ?? defaultName,
    [savedNames]
  );

  const saveForArtifact = useCallback(
    async (artifactId: Id<"exportArtifacts"> | string, filename: string) => {
      if (!canSave) return;
      try {
        await setFilename({ artifactId: artifactId as Id<"exportArtifacts">, filename });
      } catch (error) {
        notifyError(error, "Couldn't save the file name.");
      }
    },
    [canSave, setFilename]
  );

  const saveBrowserExport = useCallback(
    async (exportKind: BrowserExportKind, options: BrowserExportOptions, filename: string) => {
      if (!canSave || !owner) return;
      try {
        await recordBrowserExport({ owner, exportKind, options, filename });
      } catch (error) {
        notifyError(error, "Couldn't save the file name.");
      }
    },
    [canSave, owner, recordBrowserExport]
  );

  return { resolve, saveForArtifact, saveBrowserExport };
}
